import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield } = FiIcons;

const GuaranteeSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [showFacts, setShowFacts] = useState(false);

  const credentials = [
    {
      icon: "🎓",
      title: "Academic Excellence",
      description: "Trained at Purdue University, a top program for speech-language pathology"
    },
    {
      icon: "🏅",
      title: "Elite Recognition", 
      description: "David Knox Fellowship recipient (awarded to less than 0.02% of individuals)"
    },
    {
      icon: "🚀",
      title: "Scientific Background",
      description: "Former NASA scientist specializing in brain and cognitive development"
    },
    {
      icon: "🇺🇸",
      title: "Presidential Honor",
      description: "Outstanding Student Award recipient from President Obama"
    },
    {
      icon: "🧠",
      title: "Early Childhood Expertise",
      description: "A recognized specialist in pediatric feeding (dysphagia), birth-to-5 development, autism, FASD, and neurodevelopmental differences"
    },
    {
      icon: "🍼",
      title: "Feeding Specialist",
      description: "Advanced training in pediatric dysphagia and feeding disorders"
    },
    {
      icon: "💡",
      title: "Parent Partnership",
      description: "Believes parents are the most important part of their child's development team"
    },
    {
      icon: "❤️",
      title: "Empathetic Guide",
      description: "Brings not just expertise, but deep compassion and patience to help your child and your family feel supported and capable"
    }
  ];

  const randomFacts = [
    "Mildly obsessed with French fries",
    "Whodunit lover"
  ];

  const scrollToContact = () => {
    const contactSection = document.getElementById('contact-booking');
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-accent-light/20" ref={ref}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        {/* Guarantee Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100 max-w-4xl mx-auto">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-accent-magenta/10 rounded-full flex items-center justify-center">
                <SafeIcon icon={FiShield} className="h-8 w-8 text-accent-magenta" />
              </div>
            </div>
            
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-serif">
              See Meaningful Progress in <span className="gradient-text">30 Days</span>, Guaranteed
            </h2>
            
            <div className="space-y-6 text-gray-700 max-w-3xl mx-auto">
              <p className="font-semibold text-xl text-gray-900 font-serif">Our Promise:</p>
              <p className="leading-relaxed">
                We believe every child can grow with the right support. If you don't see measurable improvement in your child's development within 90 days, we'll work with you until you do — or offer a refund. Because your child's future matters more than our profit, and is too important to leave to chance.
              </p>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="mt-8"
            >
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={scrollToContact}
                className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold hover:bg-accent-magenta/90 transition-all duration-200 shadow-lg"
              >
                Claim Your Guarantee
              </motion.button>
            </motion.div>
          </div>
        </motion.div>

        {/* Sanna's Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h3 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-8 font-serif">
            Meet Sanna Darwish, M.S. CCC-SLP — Your Child's Development Specialist
          </h3>
          
          {/* Sanna's Photo - Using provided image */}
          <div className="flex justify-center mb-8">
            <div className="relative">
              <img
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753170088935-blob"
                alt="Sanna Darwish, M.S. CCC-SLP - Professional headshot of a woman wearing a hijab and turquoise patterned dress, smiling warmly"
                className="w-64 h-80 object-cover rounded-2xl shadow-xl"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="absolute -top-3 -left-3 w-12 h-12 bg-accent-gold/30 rounded-full blur-lg"></div>
              <div className="absolute -bottom-3 -right-3 w-16 h-16 bg-accent-magenta/30 rounded-full blur-lg"></div>
            </div>
          </div>

          {/* Bio Text */}
          <div className="max-w-3xl mx-auto mb-8 space-y-6 text-gray-700 leading-relaxed">
            <p>
              As a certified Speech-Language Pathologist with a Master's degree from Purdue University, I bring a unique blend of scientific rigor and heartfelt compassion to early intervention therapy.
            </p>
            
            <p>
              My journey began at NASA, where I specialized in brain and cognitive development research. This scientific foundation, combined with my David Knox Fellowship recognition (awarded to less than 0.02% of individuals), gives me deep insight into how developing brains learn and grow.
            </p>
            
            <p>
              What drives me every day is seeing families transform from worried and overwhelmed to confident and connected. I believe parents are the most important part of their child's development team, and my role is to empower you with the knowledge and tools to help your little one thrive.
            </p>
            
            <p className="font-semibold text-accent-magenta">
              Whether we're working on first words, safe swallowing, or building those crucial executive function foundations, I'm here to guide your family every step of the way.
            </p>
          </div>

          {/* Random Facts Section */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => setShowFacts(!showFacts)}
            className="bg-accent-gold text-gray-900 px-6 py-3 rounded-full font-semibold hover:bg-accent-gold/90 transition-all duration-200 mb-6"
          >
            {showFacts ? 'Hide' : 'Unlock'} random facts
          </motion.button>

          <AnimatePresence>
            {showFacts && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="relative mb-8"
              >
                <div className="space-y-4">
                  {randomFacts.map((fact, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.2 }}
                      className="relative bg-gradient-to-r from-accent-purple/20 to-accent-magenta/20 p-4 rounded-lg max-w-md mx-auto"
                    >
                      <p className="text-gray-800 font-medium">{fact}</p>
                      
                      {/* Arrow pointer */}
                      <motion.div
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5 + index * 0.2 }}
                        className="absolute -left-4 top-1/2 transform -translate-y-1/2"
                      >
                        <div className="w-0 h-0 border-t-2 border-b-2 border-r-4 border-t-transparent border-b-transparent border-r-accent-magenta/60"></div>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Credentials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {credentials.map((credential, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 + index * 0.1 }}
              className="bg-white p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 text-center"
            >
              <div className="text-3xl mb-4">
                {credential.icon}
              </div>
              
              <h4 className="text-lg font-bold text-gray-900 mb-3 font-serif">
                {credential.title}
              </h4>
              
              <p className="text-gray-600 leading-relaxed text-sm">
                {credential.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8 }}
          className="text-center space-y-6"
        >
          <div className="max-w-4xl mx-auto space-y-4 text-gray-700">
            <p className="font-semibold text-xl text-gray-900 font-serif">
              This isn't generic early intervention. It's specialized neurodevelopmental therapy designed for the unique needs of growing brains and worried families.
            </p>
            <p className="text-2xl font-bold gradient-text font-serif">
              Let's build your child's foundation for lifelong success—starting today.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToContact}
              className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold hover:bg-accent-magenta/90 transition-all duration-200 shadow-lg"
            >
              Start Building Today
            </motion.button>
            
            <motion.a
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              href="https://www.wethebillions.com/quiz"
              className="border-2 border-accent-magenta text-accent-magenta px-8 py-4 rounded-full font-semibold hover:bg-accent-magenta hover:text-white transition-all duration-200"
            >
              Discover Your Path
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default GuaranteeSection;